import React from "react";
import { useTranslation } from "react-i18next";
import {
  FaUsers,
  FaHardHat,
  FaClipboardCheck,
  FaSwimmingPool,
  FaFolderOpen,
  FaBuilding,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { AppScreen, AppHeader, AppCard, Tile, SectionLabel } from "../components/app";
import { useBuilding } from "../context/BuildingContext";
import { appColors, appSpacing, appType } from "../utils/appTheme";
import IconBox from "../components/app/IconBox";

/**
 * Hub Edificio (screen 09/11 del handoff). Solo navegación — cero lógica.
 */
// Textos (name/subtitle) viven en el namespace i18n "edificio", indexados por
// `key` del tile: tiles.<key>.{name,subtitle}.
const TILES = [
  { key: "reuniones", icon: <FaUsers /> },
  { key: "obras", icon: <FaHardHat /> },
  { key: "revisiones", icon: <FaClipboardCheck /> },
  { key: "amenities", icon: <FaSwimmingPool /> },
  { key: "documentacion", icon: <FaFolderOpen /> },
];

const Edificio = ({ setSelected, goBack }) => {
  const { t } = useTranslation("edificio");
  const { selectedBuilding, userUnitsInBuilding } = useBuilding();
  const name = selectedBuilding?.name || t("fallbackName");
  // Asambleas: solo propietarios vigentes (igual que el menú y el servidor).
  const isCurrentOwner = (userUnitsInBuilding || []).some(
    (u) => u.userRole === "Propietario" && !u.isHistorical
  );
  const tiles = TILES.filter((tile) => tile.key !== "reuniones" || isCurrentOwner);
  const address = selectedBuilding?.address;

  // Dirección enriquecida (pin): calle · ciudad · departamento/país — solo de
  // campos ya presentes en el edificio del context (read-only, sin queries).
  const addressLine = [
    address,
    selectedBuilding?.city,
    selectedBuilding?.department || selectedBuilding?.country,
  ]
    .filter(Boolean)
    .join(", ");

  // Trío de stats centrado (Pisos / Ascensores), derivado SOLO de campos ya
  // presentes en el doc de `building` (mismos que usa Home). Sin queries nuevas:
  // el conteo de unidades del edificio requiere consultar `apartments`, así que
  // no lo mostramos acá (según nota: si no hay dato, se omite el stat).
  const stats = [
    selectedBuilding?.floors != null && selectedBuilding?.floors !== ""
      ? { n: selectedBuilding.floors, l: t("stats.floors") }
      : null,
    selectedBuilding?.elevators != null && selectedBuilding?.elevators !== ""
      ? { n: selectedBuilding.elevators, l: t("stats.elevators") }
      : null,
  ].filter(Boolean);

  return (
    <AppScreen
      header={
        <AppHeader
          title={t("header.title")}
          onBack={goBack}
        />
      }
    >
      {/* Banner hero azul: nombre del edificio + dirección con pin.
          Único lugar donde se muestra el nombre (el header sólo dice "Edificio",
          para no repetir la misma info). Aporta dato distinto: la dirección. */}
      <AppCard variant="blue" padding={18} style={{ marginBottom: appSpacing.section }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <IconBox icon={<FaBuilding />} bg="rgba(255,255,255,.18)" color={appColors.onBlue} size={44} />
          <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
            <span
              style={{
                ...appType.cardTitle,
                color: appColors.onBlue,
                overflowWrap: "anywhere",
              }}
            >
              {name}
            </span>
            {addressLine && (
              <span
                style={{
                  ...appType.bodySm,
                  color: "rgba(255,255,255,.85)",
                  display: "inline-flex",
                  alignItems: "flex-start",
                  gap: 6,
                  overflowWrap: "anywhere",
                }}
              >
                <FaMapMarkerAlt style={{ fontSize: 11, marginTop: 3, flexShrink: 0 }} />
                {addressLine}
              </span>
            )}
          </div>
        </div>
      </AppCard>

      {/* Trío de stats con dividers verticales (número grande + label uppercase) */}
      {stats.length > 0 && (
        <AppCard style={{ marginBottom: appSpacing.section }}>
          <div style={{ display: "flex" }}>
            {stats.map((s, i) => (
              <div
                key={s.l}
                style={{
                  flex: 1,
                  minWidth: 0,
                  textAlign: "center",
                  borderLeft: i > 0 ? `1px solid ${appColors.line2}` : "none",
                }}
              >
                <div style={{ ...appType.stat }}>{s.n}</div>
                <div style={{ ...appType.statLabel, marginTop: 4 }}>{s.l}</div>
              </div>
            ))}
          </div>
        </AppCard>
      )}

      {/* Tiles de navegación del hub */}
      <SectionLabel style={{ marginBottom: 12 }}>{t("accesos")}</SectionLabel>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: appSpacing.gridGap,
        }}
      >
        {tiles.map((tile) => (
          <Tile
            key={tile.key}
            icon={tile.icon}
            name={t(`tiles.${tile.key}.name`)}
            subtitle={t(`tiles.${tile.key}.subtitle`)}
            onClick={() => setSelected && setSelected(tile.key)}
          />
        ))}
      </div>
    </AppScreen>
  );
};

export default Edificio;
